'use strict';

/**
 * @fileoverview `@puffin-core/retry-policy` — the ONE rule for "may this request be re-sent AUTOMATICALLY?"
 * (CLIENT-RETRY-IDEMPOTENCY-001 U1).
 *
 * POS-STATION's walk (2026-10-29) saw ONE opening of pos-web's signup email step send FOUR
 * `POST /v1/signup/verify-email`: the interceptor judged the error's CLASS and never the METHOD, so a write the server
 * may already have committed went out again, three times. The census behind this package found SEVEN hand-rolled HTTP
 * retry classifiers, and a second retry layer that compounded to twelve sends. This module is the ONE rule they read.
 *
 * A request is re-sent automatically only when BOTH hold (THINKPAD, 2026-10-29, the plan as accepted):
 *
 * 1. **The failure is retryable.** A transport failure (no answer), a timeout (incl. 408), a 500 / 502 / 504; a 429
 *    after its `Retry-After`; a BARE 503 (no coded envelope: a load balancer during a deploy) on a replay-safe request.
 *    NEVER a 503 carrying a coded envelope (our own named refusal: it will answer the same), another 4xx, or an abort.
 *    The ONE exception is `RETRYABLE_REFUSAL_CODES`: a KEYED request refused `503 IDEMPOTENCY_STORE_UNAVAILABLE`.
 * 2. **Repeating it cannot do the work twice.** The method is safe (GET, HEAD, OPTIONS); or the request carries an
 *    `Idempotency-Key` (the receiver replays it, IDEM-HTTP-001); or the failure PROVES the request never arrived (a
 *    429 — a refusal before any work — or a connection refused / a name that did not resolve, before a byte was sent);
 *    or the request is a SANCTIONED replay-safe call named in `SANCTIONED_REPLAY_SAFE` with its reason.
 *
 * PUT and DELETE are NOT safe here: idempotent in HTTP's grammar is not idempotent in fact, and the rule never asks a
 * caller to audit one route. A browser's status 0 or a timeout proves NOTHING about arrival: the request may have run.
 *
 * The rule answers whether and how long; it never sleeps and never sends. Each engine keeps its own attempt budget and
 * backoff, and asks this module before every re-send.
 *
 * @module @puffin-core/retry-policy
 */

const { retryAfterMsOf } = require('@puffin-core/retry-after');

/** The methods that change nothing on the server (RFC 9110 §9.2.1). */
const SAFE_METHODS = Object.freeze(['GET', 'HEAD', 'OPTIONS']);

/** The request header a receiver replays a write by (IDEM-HTTP-001), compared case-insensitively. */
const IDEMPOTENCY_KEY_HEADER = 'idempotency-key';

/**
 * Transport codes raised BEFORE a request left: the connection was refused, or the host never resolved. Server-side
 * only — a browser exposes no such code, so a browser failure never proves non-arrival.
 */
const NOT_SENT_TRANSPORT_CODES = Object.freeze([
  'ECONNREFUSED',
  'ENOTFOUND',
  'EAI_AGAIN',
  'ENETUNREACH',
  'EHOSTUNREACH',
]);

/** Transport codes of a request whose fate is unknown: it may have reached the server and run. */
const IN_DOUBT_TRANSPORT_CODES = Object.freeze([
  'ECONNRESET',
  'ETIMEDOUT',
  'ECONNABORTED',
  'EPIPE',
  'ERR_NETWORK',
  'UND_ERR_SOCKET',
]);

/** Statuses a server answers when the work may succeed on another attempt. 503 is judged on its own (coded or bare). */
const RETRYABLE_STATUSES = Object.freeze([408, 500, 502, 504]);

/**
 * The coded 503 refusals a KEYED request is re-sent after (with the same key, after the `Retry-After`). A closed list
 * of ONE (THINKPAD, 1:22 PM PDT 2026-10-29): `IDEMPOTENCY_STORE_UNAVAILABLE` is the idempotency guard's answer when it
 * could not check the key against earlier attempts. It refuses before any work runs (owner ruling Q-OR-4, fail closed),
 * so the same key replays safely, and the guard names the wait. Every other coded 503 answers the same on a re-send.
 */
const RETRYABLE_REFUSAL_CODES = Object.freeze(['IDEMPOTENCY_STORE_UNAVAILABLE']);

/**
 * Why the rule answered as it did. A closed vocabulary, for logs and metrics; never shown to a person.
 * @enum {string}
 */
const RETRY_REASONS = Object.freeze({
  ABORTED: 'aborted',
  RATE_LIMITED: 'rate-limited',
  NOT_SENT: 'not-sent',
  CODED_REFUSAL: 'coded-refusal',
  STORE_UNAVAILABLE: 'idempotency-store-unavailable',
  REFUSED: 'refused',
  REPLAY_SAFE: 'replay-safe',
  IN_DOUBT: 'in-doubt-write',
  NOT_RETRYABLE: 'not-retryable',
  SESSION_UNREACHABLE: 'session-unreachable',
});

/**
 * The failure a request carries when its session refresh could not be answered (`@puffin-core/api-client`
 * `SessionUnreachableError`, `code: 'SESSION_UNREACHABLE'`, POS-SESSION-STATION-TIMERS-001 U7c / U15). The request is
 * rejected as a network failure that KEEPS this as its cause; a re-send would only meet the same 401 with the same
 * stale token, so it is never re-sent, whatever the method.
 */
const SESSION_UNREACHABLE_CODE = 'SESSION_UNREACHABLE';

/**
 * The SANCTIONED replay-safe requests: a write-shaped method whose repeat cannot do work twice IN FACT. Each names its
 * method, its kind, the one file that may name it, and why. A key not in this table THROWS (a typo can never sanction
 * a write); a key used with another method THROWS. The `autoRetryJudgedByPolicy` contract proves every row is named by
 * its file and no other.
 *
 * Kinds: `post-read` — a read sent as POST (it writes nothing); `chunk-put` — the same bytes PUT at the same index;
 * `at-least-once` — a delivery the receiver dedupes on a stable id it is sent (a webhook's X-Webhook-Delivery).
 */
const SANCTIONED_REPLAY_SAFE = Object.freeze({
  'planner-web:forecast-batch': Object.freeze({
    method: 'POST',
    kind: 'post-read',
    file: 'apps/planner-web/src/features/sales/hooks/useMethodProjections.js',
    reason:
      '`POST /analytics/forecast/batch` is answered by ForecastBatchService.runBatch, which writes nothing.',
  }),
  'api-client:chunk-put': Object.freeze({
    method: 'PUT',
    kind: 'chunk-put',
    file: 'packages/api-client/lib/esm/chunkedUpload.js',
    reason:
      'A chunk is PUT to its own index: the same bytes at the same place, whatever the attempt.',
  }),
  'webhooks:stable-delivery': Object.freeze({
    method: 'POST',
    kind: 'at-least-once',
    file: 'packages/webhooks/lib/WebhookDispatcher.js',
    reason:
      'A webhook delivery whose X-Webhook-Delivery id is STABLE (derived from the event id and the URL): the subscriber dedupes on that id, the webhook contract. Passed only when the id is stable (THINKPAD 1:04 PM PDT 2026-10-29).',
  }),
});

/**
 * Read one header from any header bag the ecosystem carries: a WHATWG `Headers`, an AxiosHeaders, a `Map`, or a plain
 * object with any key case.
 *
 * @param {*} headers
 * @param {string} name - lower-case
 * @returns {string|null}
 */
function headerOf(headers, name) {
  if (!headers || typeof headers !== 'object') return null;
  if (typeof headers.get === 'function') {
    const value = headers.get(name);
    if (value !== undefined && value !== null) return String(value);
  }
  for (const [key, value] of Object.entries(headers)) {
    if (key.toLowerCase() === name && value !== undefined && value !== null) return String(value);
  }
  return null;
}

/** The status a failure was answered with, from every error shape the transports raise; `null` when none answered. */
function statusOf(error) {
  const candidates = [error.status, error.statusCode, error.response?.status];
  const status = candidates.find((value) => Number.isInteger(value) && value > 0);
  return status ?? null;
}

/** The response headers a failure carries (for `Retry-After`), from every error shape. */
function responseHeadersOf(error) {
  return error.response?.headers ?? error.headers ?? null;
}

/**
 * The code of the failure's CODED error envelope (RES-ENV-001 `{ error: { code } }`): our server's named refusal; `null`
 * when it carries none. Read from the facade (`body`), the normalised transport error (`data`), the raw axios error
 * (`response.data`), and the facade's promoted `errorCode`.
 */
function refusalCodeOf(error) {
  const isCode = (code) => typeof code === 'string' && code !== '';
  const envelope = [error.body, error.data, error.response?.data].find(
    (body) => body && typeof body === 'object' && isCode(body.error?.code),
  );
  if (envelope) return envelope.error.code;
  return isCode(error.errorCode) ? error.errorCode : null;
}

/** Whether the request carries a non-blank `Idempotency-Key`. */
function isKeyed(headers) {
  const key = headerOf(headers, IDEMPOTENCY_KEY_HEADER);
  return key !== null && key.trim() !== '';
}

function isAbort(error) {
  return (
    error.name === 'AbortError' ||
    error.name === 'CanceledError' ||
    error.code === 'ERR_CANCELED' ||
    error.code === 'ABORT_ERR'
  );
}

function transportCodeOf(error) {
  const code = error.code ?? error.cause?.code;
  return typeof code === 'string' ? code : null;
}

/**
 * The ONE sanctioned entry a request names, validated against its method.
 *
 * @param {string|undefined} sanctioned
 * @param {string} method
 * @returns {boolean}
 * @throws {TypeError} An unknown key, or a key used with another method.
 */
function sanctionedFor(sanctioned, method) {
  if (sanctioned === undefined) return false;
  const entry = Object.hasOwn(SANCTIONED_REPLAY_SAFE, sanctioned)
    ? SANCTIONED_REPLAY_SAFE[sanctioned]
    : null;
  if (!entry) {
    throw new TypeError(`retry-policy: "${sanctioned}" is not a sanctioned replay-safe request`);
  }
  if (entry.method !== method) {
    throw new TypeError(
      `retry-policy: "${sanctioned}" is sanctioned for ${entry.method}, not ${method}`,
    );
  }
  return true;
}

/**
 * Whether repeating this request cannot do its work twice.
 *
 * @param {{ method: string, headers?: *, sanctioned?: string }} request
 * @returns {boolean}
 * @throws {TypeError} No method, or a bad sanctioned key.
 */
function isReplaySafeRequest({ method, headers, sanctioned } = {}) {
  if (typeof method !== 'string' || method.trim() === '') {
    throw new TypeError('retry-policy: a request names its method');
  }
  const verb = method.trim().toUpperCase();
  if (sanctionedFor(sanctioned, verb)) return true;
  if (SAFE_METHODS.includes(verb)) return true;
  return isKeyed(headers);
}

/**
 * May this failed request be re-sent automatically, and after how long?
 *
 * @param {{ method: string, headers?: *, sanctioned?: string, error: * }} input - the request as it was sent, and the
 *   failure it met (any transport's error shape)
 * @returns {{ retry: boolean, waitMs: number|null, reason: string }} `waitMs` is the server's `Retry-After` when it
 *   named one (else `null`: the engine's own backoff); only a `retry: true` answer carries a wait.
 * @throws {TypeError} No method, no error, or a bad sanctioned key.
 */
function mayAutoRetry({ method, headers, sanctioned, error } = {}) {
  if (!error || typeof error !== 'object') {
    throw new TypeError('retry-policy: a decision needs the failure it judges');
  }
  const replaySafe = isReplaySafeRequest({ method, headers, sanctioned });
  const no = (reason) => ({ retry: false, waitMs: null, reason });
  const yes = (reason) => ({
    retry: true,
    waitMs: retryAfterMsOf(responseHeadersOf(error)),
    reason,
  });

  if (isAbort(error)) return no(RETRY_REASONS.ABORTED);
  if (error.code === SESSION_UNREACHABLE_CODE || error.cause?.code === SESSION_UNREACHABLE_CODE) {
    return no(RETRY_REASONS.SESSION_UNREACHABLE);
  }

  const status = statusOf(error);
  if (status === 429) return yes(RETRY_REASONS.RATE_LIMITED);
  if (status === 503) {
    const code = refusalCodeOf(error);
    if (code !== null) {
      return RETRYABLE_REFUSAL_CODES.includes(code) && isKeyed(headers)
        ? yes(RETRY_REASONS.STORE_UNAVAILABLE)
        : no(RETRY_REASONS.CODED_REFUSAL);
    }
    return replaySafe ? yes(RETRY_REASONS.REPLAY_SAFE) : no(RETRY_REASONS.IN_DOUBT);
  }
  if (status !== null && !RETRYABLE_STATUSES.includes(status)) return no(RETRY_REASONS.REFUSED);

  if (status === null) {
    const code = transportCodeOf(error);
    if (code !== null && NOT_SENT_TRANSPORT_CODES.includes(code))
      return yes(RETRY_REASONS.NOT_SENT);
    const transportFailure =
      code === null || IN_DOUBT_TRANSPORT_CODES.includes(code) || error.name === 'TimeoutError';
    if (!transportFailure) return no(RETRY_REASONS.NOT_RETRYABLE);
  }

  return replaySafe ? yes(RETRY_REASONS.REPLAY_SAFE) : no(RETRY_REASONS.IN_DOUBT);
}

/**
 * Mint a fresh `Idempotency-Key`: `<namespace>:<uuid>`. Moved UNCHANGED out of `@puffin-core/api-client`'s submit-key
 * source (CLIENT-RETRY-IDEMPOTENCY-001; THINKPAD, 1:24 PM PDT 2026-10-29), so the browser log shipper keys a batch with
 * this mint instead of a copy; api-client re-exports it identity-pinned.
 *
 * A key minted per CLICK is never right: a key lives as long as its ACT, held by its holder across every retry (a
 * submit's key source or store, a log batch). So a use of this function is a MINT, and `web/no-per-call-idempotency-key`
 * reports one in any file but the named holders (its mint arm; Q-R8PORT3-7, which this export's privacy in api-client
 * used to hold). A poll — a mutation that must run again on every click — carries NO key (IDEM-HTTP-001 `exempt(req)`).
 *
 * `crypto.randomUUID` needs a secure context; every web app is served over https in deployed environments and over
 * `localhost` in dev, both of which qualify. No fallback: an unreachable branch is untestable error handling (GEN-0),
 * and every fallback shape in this repo reaches for `Date.now()`, the very thing a key must never carry.
 *
 * @param {string} namespace - Short operation name, e.g. `payday-payroll-submit`.
 * @returns {string}
 */
function mintIdempotencyKey(namespace) {
  // `globalThis.crypto` — the Web Crypto global in browsers AND Node 18+, so
  // the one spelling lints clean in this dual-consumed package.
  return `${namespace}:${globalThis.crypto.randomUUID()}`;
}

module.exports = {
  IDEMPOTENCY_KEY_HEADER,
  IN_DOUBT_TRANSPORT_CODES,
  NOT_SENT_TRANSPORT_CODES,
  RETRY_REASONS,
  RETRYABLE_REFUSAL_CODES,
  RETRYABLE_STATUSES,
  SAFE_METHODS,
  SANCTIONED_REPLAY_SAFE,
  SESSION_UNREACHABLE_CODE,
  isReplaySafeRequest,
  mayAutoRetry,
  mintIdempotencyKey,
};
